import Vue from 'vue';

import Localization from '@/services/Localization';
import { DirectiveBinding } from 'vue/types/options';

//
// Every string that needs localization should use this directive.
// The directive fills the innerHTML of the element that it is embedded in
// with the localized value based on the input localization key.
//
// Usage:
//
//  Most use cases will find the simple interface sufficient.
//  More intricate scenarios, e.g. string interpolation, will have to
//  use the second interface.
//
//  Simple interface:
//
//    <span v-localize="localization_key"/>
//
//    localizations["localization_key"] will be put into the span.
//
//  Comprehensive interface:
//
//    <span
//      v-localize="{
//        preprocess: preprocessFunc,
//        callback: callbackFunc,
//        keys:["localization_key1", "localization_key2"]
//      }"
//    />
//
//    preprocessFunc([ localizations["localization_key1"], localizations["localization_key2"] ])
//    will be placed inside the span. Subsequently, callbackFunc will be invoked (with no arguments).
//    preprocessFunc and callbackFunc are optional.
//

const identityFunc = (arg: any) => {
  return arg;
};

const implementation = (el: HTMLElement, binding: DirectiveBinding): void => {
  // optional
  let preprocess = binding.value.preprocess;
  if (!preprocess) {
    preprocess = identityFunc;
  }
  // optional
  let callback = binding.value.callback;
  if (!callback) {
    callback = identityFunc;
  }
  // NOT optional
  if (!binding.value) {
    throw 'No key to localize';
  }
  let keys: string[] = binding.value.keys;
  if (!keys) {
    keys = [binding.value];
  }
  if (keys.length === 0) {
    throw 'No key(s) to localize';
  }

  const promises = keys.map(Localization);
  Promise.all(promises).then((values) => {
    el.innerHTML = preprocess(values);
    callback();
  });
};

export const LocalizationDirective = {
  inserted(el: HTMLElement, binding: DirectiveBinding): void {
    return implementation(el, binding);
  },
  // We need this to support data binding i.e. when the value
  // passed to the directive changes dynamically.
  update(el: HTMLElement, binding: DirectiveBinding): void {
    return implementation(el, binding);
  },
};

Vue.directive('localize', LocalizationDirective);
